import React, { ReactElement } from 'react';
import {
  useProfile,
  useIsAdmin,
  useIsOrganizationLeader,
  useIsFullWidth,
} from 'hooks';
import { GetSchoolResponse } from 'services/api';
import { SectionReveal } from './shared';
import {
  DEFAULT_CAMPUS_RESOURCES,
  CAMPUS_RESOURCE_FIELDS,
  CampusResource,
} from './shared/resources.config';
import {
  SchoolHeader,
  CampusResourcesWidget,
  ActionRequiredList,
} from './sections';

export interface BrowseSidebarParts {
  banner: ReactElement;

  desktopSidebar: ReactElement;

  mobileBefore: ReactElement | null;
  mobileAfter: ReactElement | null;
}

export const useBrowseSidebar = (
  school?: GetSchoolResponse,
): BrowseSidebarParts => {
  const { profile } = useProfile();
  const isDesktop = useIsFullWidth();
  const isAdmin = useIsAdmin();
  const isLeader = useIsOrganizationLeader();
  const adminEnabled = Boolean(isAdmin || isLeader);

  const schoolId = profile?.school?.id;
  const schoolName = school?.name ?? profile?.school?.name;
  const bannerUrl = school?.bannerUrl;
  const iconUrl = school?.iconUrl ?? profile?.school?.iconUrl;

  const banner = (
    <SchoolHeader
      name={schoolName}
      bannerUrl={bannerUrl}
      iconUrl={iconUrl}
      loading={!profile}
    />
  );

  const configuredResources: CampusResource[] = DEFAULT_CAMPUS_RESOURCES.map(
    resource => {
      const field = CAMPUS_RESOURCE_FIELDS.find(f => f.id === resource.id);
      const href = field ? school?.campusResourceUrls?.[field.key] : undefined;
      return { ...resource, href };
    },
  ).filter(resource => Boolean(resource.href));

  const campusResources = configuredResources.length ? (
    <CampusResourcesWidget resources={configuredResources} />
  ) : null;

  const actionRequired = (
    <ActionRequiredList
      schoolId={schoolId}
      enabled={adminEnabled}
      showEyebrow={!isDesktop}
    />
  );

  const desktopSidebar = (
    <>
      <SectionReveal index={0} component="header" ariaLabel="School">
        {banner}
      </SectionReveal>
      {adminEnabled && (
        <SectionReveal
          index={1}
          ariaLabel="Action required"
          sx={{ px: '20px' }}
        >
          {actionRequired}
        </SectionReveal>
      )}
      {campusResources && (
        <SectionReveal
          index={2}
          ariaLabel="Campus resources"
          sx={{ px: '20px' }}
        >
          {campusResources}
        </SectionReveal>
      )}
    </>
  );

  const mobileBefore = null;

  const mobileAfter = campusResources ? (
    <SectionReveal index={5} ariaLabel="Campus resources">
      {campusResources}
    </SectionReveal>
  ) : null;

  return { banner, desktopSidebar, mobileBefore, mobileAfter };
};
